import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import { bazaarUrl } from "@/mk2/apps/bazaar/urls";
import { blogsMagazineFeedUrl, blogsUrl } from "@/mk2/apps/blogs/urls";
import { contestsHomeUrl } from "@/mk2/apps/contests/urls";
import { counsellingsHomeUrl } from "@/mk2/apps/counsellings/urls";
import { forumUrl } from "@/mk2/apps/forum/urls";
import { homepageUrl } from "@/mk2/apps/home/urls";
import { strollersUrl } from "@/mk2/apps/strollers/urls";
import { loginUrl, signupUrl } from "@/mk2/apps/users/urls";
import { UNLISTED_WIKI_CATEGORIES, WIKI_REVIEWS_ARTICLE_SLUG } from "@/mk2/apps/wiki/helpers";
import { getWikiArticleUrl } from "@/mk2/apps/wiki/urls";
import { wikiCategoryUrl } from "@/mk2/apps/wiki/utils";
import { FooterNarrow } from "@/mk2/components/Footer";
import { Img } from "@/mk2/components/Img";
import { Link } from "@/mk2/components/Link";
import { MobileMenuItem } from "@/mk2/components/MobileMenuItem";
import { modrykonikLogoUrl, url } from "@/mk2/helpers/urls";
import { UserEntity } from "@/mk2/schemas";
import {
    ASK_AN_EXPERT,
    BAZAAR,
    BLOGS_WALL,
    CONTESTS,
    FORUMS_AND_GROUPS,
    LOG_IN,
    MAGAZINE,
    MAIN_MENU_REVIEWS,
    REGISTER_HERE,
    SERVER_FULL_NAME,
    SERVER_SLOGAN,
    STROLLER_STROLLEROPEDIA,
} from "@/mk/autogenerated/translations/MobileMenuAnon.ed90dee1a84e6fcd5c8015179f893668";
import { WIKI_CATEGORIES } from "@/mk/autogenerated/wikiCategories";
import { hasAds } from "@/mk/bazaar/common/userUtils";

import styles from "./MobileMenu.mscss";

interface ContentProps {
    currentUrl: string;
    activeSection?: string;
    activeItem?: string;
    requestUser: UserEntity;
    onItemClick();
}

/**
 * The anonymous (logged-out) mobile menu body - pure static links, no badges or user-specific
 * data. Single source of truth shared by the lazy `MobileMenu` (anon branch) and the always-in-DOM
 * `MobileMenuAnonPanel` so the two can never drift.
 */
export class MobileMenuAnonContent extends React.PureComponent<ContentProps> {
    public render() {
        const { currentUrl, activeSection, activeItem, requestUser, onItemClick } = this.props;

        return (
            <div className={styles.MobileMenu__mobileMenu}>
                <ul className={styles.MobileMenu__menuItemBlock}>
                    <MobileMenuItem
                        title={REGISTER_HERE}
                        itemUrl={url(
                            signupUrl,
                            {},
                            {
                                mkutm_campaign: "menu_registration",
                                mkutm_source: "modrykonik",
                                next: currentUrl,
                            },
                        )}
                        isActive={false}
                        isMainSection
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={LOG_IN}
                        itemUrl={url(loginUrl, {}, { next: currentUrl })}
                        isActive={false}
                        onItemClick={onItemClick}
                    />
                </ul>
                <ul className={styles.MobileMenu__menuItemBlock}>
                    <MobileMenuItem
                        title={BAZAAR}
                        itemUrl={bazaarUrl}
                        isActive={activeSection === "bazaar"}
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={FORUMS_AND_GROUPS}
                        itemUrl={forumUrl}
                        isActive={activeSection === "forum"}
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={ASK_AN_EXPERT}
                        itemUrl={counsellingsHomeUrl}
                        isActive={activeSection === "counsellings"}
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={MAGAZINE}
                        itemUrl={blogsMagazineFeedUrl}
                        isActive={activeSection === "magazine"}
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={BLOGS_WALL}
                        itemUrl={blogsUrl}
                        isActive={activeSection === "photoblog"}
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={STROLLER_STROLLEROPEDIA}
                        itemUrl={strollersUrl}
                        isActive={activeSection === "strollers"}
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={CONTESTS}
                        itemUrl={contestsHomeUrl}
                        isActive={activeSection === "contests"}
                        onItemClick={onItemClick}
                    />
                    <MobileMenuItem
                        title={MAIN_MENU_REVIEWS}
                        itemUrl={getWikiArticleUrl(
                            WIKI_REVIEWS_ARTICLE_SLUG,
                            WIKI_REVIEWS_ARTICLE_SLUG,
                        )}
                        isActive={
                            activeSection === "wiki" && activeItem === WIKI_REVIEWS_ARTICLE_SLUG
                        }
                        onItemClick={onItemClick}
                    />
                </ul>

                <ul className={styles.MobileMenu__menuItemBlock}>
                    {WIKI_CATEGORIES.filter(
                        (wikiCategory) => !UNLISTED_WIKI_CATEGORIES.includes(wikiCategory.id),
                    ).map((wikiCategory) => (
                        <MobileMenuItem
                            key={wikiCategory.id}
                            title={wikiCategory.name}
                            itemUrl={wikiCategoryUrl(wikiCategory)}
                            isActive={activeSection === "wiki" && activeItem === wikiCategory.slug}
                            onItemClick={onItemClick}
                            allowDataGoogleInterstitial={
                                hasAds(requestUser) && wikiCategory.id === 9
                            }
                        />
                    ))}
                </ul>
            </div>
        );
    }
}

interface PanelProps {
    currentUrl: string;
    activeSection?: string;
    activeItem?: string;
    requestUser: UserEntity;
    onItemClick();
}

interface OffCanvasProps {
    currentUrl: string;
    activeSection?: string;
    activeItem?: string;
    requestUser: UserEntity;
}

/**
 * Self-contained, pure-CSS off-canvas wrapper around the anonymous menu. The checkbox
 * `#mobileMenuTrigger` + overlay + sliding panel are DOM siblings toggled purely via CSS
 * (`:checked ~` rules in MobileMenu.mscss), so this unit carries no React state and can be
 * rendered anywhere in the DOM - we mount it after the main content so crawlers/agents read
 * real content first. The hamburger (Header) and close X (panel) toggle it by id.
 */
export class MobileMenuAnonOffCanvas extends React.PureComponent<OffCanvasProps> {
    public render() {
        const { currentUrl, activeSection, activeItem, requestUser } = this.props;

        return (
            <div className={styles.MobileMenu__offCanvas}>
                <input
                    className={styles.MobileMenu__offCanvasTrigger}
                    id="mobileMenuTrigger"
                    type="checkbox"
                />
                <label
                    htmlFor="mobileMenuTrigger"
                    className={styles.MobileMenu__offCanvasOverlay}
                />
                <div className={styles.MobileMenu__offCanvasPanel}>
                    <MobileMenuAnonPanel
                        currentUrl={currentUrl}
                        activeSection={activeSection}
                        activeItem={activeItem}
                        requestUser={requestUser}
                        onItemClick={this.closeMenu}
                    />
                </div>
            </div>
        );
    }

    private closeMenu = () => {
        const trigger = document.getElementById("mobileMenuTrigger") as HTMLInputElement | null;
        if (trigger) {
            trigger.checked = false;
        }
    };
}

/**
 * Always-in-DOM, SSR-rendered off-canvas copy of the anonymous mobile menu. Rendered for logged-out
 * visitors (and crawlers) so mobile-first indexing sees the nav + footer links, which otherwise live
 * only inside the lazy, mount-on-open hamburger. Hidden off-screen via CSS (not display:none).
 */
export class MobileMenuAnonPanel extends React.PureComponent<PanelProps> {
    public render() {
        const { currentUrl, activeSection, activeItem, requestUser, onItemClick } = this.props;

        return (
            <div className={styles.MobileMenu}>
                <div className={styles.MobileMenu__mobileHeader}>
                    <label
                        htmlFor="mobileMenuTrigger"
                        className={cx(
                            styles.MobileMenu__userIcon,
                            "topmenu-userIcon",
                            styles["MobileMenu__mobileHeader--mobileClose"],
                        )}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </label>
                </div>
                <div className={styles.MobileMenu__mobileInner}>
                    <div className={styles.MobileMenu__mobileInnerRow}>
                        <Link
                            className={styles.MobileMenu__mobileLogo}
                            title={SERVER_FULL_NAME + " - " + SERVER_SLOGAN}
                            to={homepageUrl}
                            onClick={onItemClick}
                        >
                            <Img
                                height={30}
                                width={132}
                                alt={SERVER_FULL_NAME + " - " + SERVER_SLOGAN}
                                src={modrykonikLogoUrl()}
                            />
                        </Link>
                    </div>
                    <MobileMenuAnonContent
                        currentUrl={currentUrl}
                        activeSection={activeSection}
                        activeItem={activeItem}
                        requestUser={requestUser}
                        onItemClick={onItemClick}
                    />
                </div>
                <FooterNarrow onItemClick={onItemClick} isMobile />
            </div>
        );
    }
}
